import { Component, Input } from "@angular/core";
import { tap } from "rxjs/operators";
import { State } from "@progress/kendo-data-query";
import {
  ServiceConfigurationKeysDataService,
  EnvironmentServiceConfigurationDataService,
  TenantEnvironmentServiceConfigurationDataService,
} from "../service/service-configuration-keys-data.service";
import { Service } from "../models/service";
import { EnvironmentService } from "../models/environment-service";
import {
  ServiceConfigurationScope,
  ServiceConfigurationKey,
  EnvironmentServiceConfiguration,
  TenantEnvironmentServiceConfiguration,
  ServiceConfigurationKeyScoped,
} from "../models/configuration-setting";
import { TenantEnvironment } from "../models/tenant";
import { DialogRef, DialogService } from "@progress/kendo-angular-dialog";
import { ExternalEditGridComponent } from 'src/app/core/external-edit-grid.component';
import { ConfigurationCategoryDataService } from '../service/configuration-category-data.service';
import { ConfigurationCategory } from '../models/configuration-category';
import { UxConstants } from 'src/app/ux-constants';

@Component({
  selector: "app-service-configuration-keys",
  templateUrl: "./service-configuration-keys.component.html",
  styleUrls: ["./service-configuration-keys.component.scss"],
})
export class ServiceConfigurationKeysComponent extends ExternalEditGridComponent<ServiceConfigurationKeyScoped> {
  public title: string;
  @Input() public service: Service;
  @Input() public environmentService: EnvironmentService;
  @Input() public tenantEnvironment: TenantEnvironment;
  public scope: ServiceConfigurationScope;
  public categories: ConfigurationCategory[];
  public gridState: State = {
    sort: [],
    skip: 0,
    take: 10,
  };
  private serviceId: number;
  private environmentServiceId: number;
  private tenantEnvironmentId: number;

  constructor(
    private serviceConfigurationKeyDataService: ServiceConfigurationKeysDataService,
    private environmentServiceConfigurationDataService: EnvironmentServiceConfigurationDataService,
    private tenantEnvironmentServiceConfigurationDataService: TenantEnvironmentServiceConfigurationDataService,
    private categoryDataService: ConfigurationCategoryDataService,
    private dialogService: DialogService
  ) {
    super(ServiceConfigurationKeyScoped);
  }

  ngOnInit() {
    this.scope =
      this.tenantEnvironment != null
        ? ServiceConfigurationScope.Tenant
        : this.environmentService != null
        ? ServiceConfigurationScope.Environment
        : ServiceConfigurationScope.Service;

      if (this.scope === ServiceConfigurationScope.Service) {
        this.serviceId = this.service.Id;
      } else if (this.scope === ServiceConfigurationScope.Environment) {
        this.serviceId = this.environmentService.ServiceId;
        this.environmentServiceId = this.environmentService.Id;
      } else {
        this.serviceId = this.environmentService.ServiceId;
        this.environmentServiceId = this.environmentService.Id;
        this.tenantEnvironmentId = this.tenantEnvironment.Id;
      }

    this.categoryDataService.getEntities('')
    .subscribe(r => {
      this.categories = r.value;
      let categoriesToSet: ConfigurationCategory[] = [];
      let defaultCat = new ConfigurationCategory();
      defaultCat.Id = -1;
      defaultCat.Name = '';
      categoriesToSet.push(defaultCat);
      this.categories.forEach(c => {
        categoriesToSet.push(c);
      });

      this.categories = categoriesToSet;
    });

    super.ngOnInit();
  }

  protected refresh() {
    this.view = this.serviceConfigurationKeyDataService
      .getFlat(
        this.gridState,
        this.serviceId,
        this.environmentServiceId,
        this.tenantEnvironmentId
      )
      .pipe(
        tap((d) => {
          for (const dataItem of d.data) {
            // category
            if (dataItem.Category) {
              dataItem.CategoryName = dataItem.Category.Name;
            } else {
              dataItem.CategoryName = '';
            }
            // environment
            if (dataItem.EnvironmentServiceConfiguration?.length > 0) {
              dataItem.EnvironmentServiceId =
                dataItem.EnvironmentServiceConfiguration[0].EnvironmentServiceId;
              dataItem.EnvironmentServiceConfigurationId =
                dataItem.EnvironmentServiceConfiguration[0].Id;
              dataItem.EnvironmentValue =
                dataItem.EnvironmentServiceConfiguration[0].Value;
            }
            // tenant
            if (dataItem.TenantEnvironmentServiceConfiguration?.length > 0) {
              dataItem.TenantEnvironmentId =
                dataItem.TenantEnvironmentServiceConfiguration[0].TenantEnvironmentId;
              dataItem.TenantEnvironmentServiceConfigurationId =
                dataItem.TenantEnvironmentServiceConfiguration[0].Id;
              dataItem.TenantValue =
                dataItem.TenantEnvironmentServiceConfiguration[0].Value;
            }
            if (dataItem.IsSecret) {
              dataItem.EffectiveValue = UxConstants.secretMask;
            }
            else {
              dataItem.EffectiveValue = dataItem.Value;
              if (this.scope == ServiceConfigurationScope.Environment && dataItem.EnvironmentValue && dataItem.EnvironmentValue.length > 0)
                dataItem.EffectiveValue = dataItem.EnvironmentValue;
              if (this.scope == ServiceConfigurationScope.Tenant) {
                if (dataItem.TenantValue && dataItem.TenantValue.length > 0) {
                  dataItem.EffectiveValue = dataItem.TenantValue;
                }
                else if (dataItem.EnvironmentValue && dataItem.EnvironmentValue.length > 0) {
                  dataItem.EffectiveValue = dataItem.EnvironmentValue;
                }
              } 
            }            
          }
          this.loading = false;
        })
      );
  }

  public addHandler(){
    super.addHandler();
    this.editItem.ServiceId = this.serviceId;
    this.editItem.EnvironmentServiceId = this.environmentServiceId;
    this.editItem.TenantEnvironmentId = this.tenantEnvironmentId;
    this.editItem.AllowEnvironmentOverride = false;
    this.editItem.AllowTenantOverride = false;
    this.editItem.IsSecret = false;
  }

  public saveHandler(configKey: ServiceConfigurationKeyScoped) {
    this.loading = true;
   
    if (this.scope === ServiceConfigurationScope.Service) {
      let entity = new ServiceConfigurationKey();
      entity.Id = configKey.Id,
      entity.ServiceId = this.serviceId;
      entity.Name = configKey.Name;
      entity.Value = configKey.Value;
      entity.Description = configKey.Description;
      entity.AllowEnvironmentOverride = configKey.AllowEnvironmentOverride;
      entity.AllowTenantOverride = configKey.AllowTenantOverride;
      entity.IsSecret = configKey.IsSecret;
      entity.CategoryId = configKey.CategoryId === -1 ? null : configKey.CategoryId;

      this.refreshObservable(
        this.serviceConfigurationKeyDataService.update(entity, this.isNew)
      );
    } else if (this.scope == ServiceConfigurationScope.Environment) {
      let entity = new EnvironmentServiceConfiguration();
      entity.Id = configKey.EnvironmentServiceConfigurationId;
      entity.EnvironmentServiceId = this.environmentService.Id;
      entity.ServiceConfigurationKeyId = configKey.Id;
      entity.Value = configKey.EnvironmentValue;

      this.refreshObservable(
        this.environmentServiceConfigurationDataService.update(
          entity,
          entity.Id === 0
        )
      );      
    } else if (this.scope == ServiceConfigurationScope.Tenant) {
      let entity = new TenantEnvironmentServiceConfiguration();
      entity.Id = configKey.TenantEnvironmentServiceConfigurationId;
      entity.TenantEnvironmentId = this.tenantEnvironment.Id;
      entity.ServiceConfigurationKeyId = configKey.Id;
      entity.Value = configKey.TenantValue;

      this.refreshObservable(
        this.tenantEnvironmentServiceConfigurationDataService.update(
          entity,
          entity.Id === 0
        )
      );      
    }

    this.editItem = undefined;
  }

  public removeHandler({ dataItem }) {
    let dialog: DialogRef;
    let deleteAction: any;
    const cancelAction = { text: "Cancel" };
    if (this.scope === ServiceConfigurationScope.Service) {
      this.environmentServiceConfigurationDataService.overrideCount(+dataItem.Id)
      .subscribe(ec => 
        {
          if (+ec['@odata.count'] > 0) {
            let phrase = 'environment';
            if (+ec['@odata.count'] > 1) {
              phrase = phrase + 's';
            }
            this.dialogService.open({
              title: "Cannot Delete",
              content: `This configuration key is overridden by ${ec['@odata.count']} ${phrase}`,
              actions: [cancelAction],
            });
            return;
          }

          this.tenantEnvironmentServiceConfigurationDataService.overrideCount(+dataItem.Id)
          .subscribe(tc => 
            {
              if (tc['@odata.count'] > 0) {
                let phrase = 'tenant';
                if (+tc['@odata.count'] > 1) {
                  phrase = 'tenants';
                }
                this.dialogService.open({
                  title: "Cannot Delete",
                  content: `This configuration key is overridden by ${tc['@odata.count']} ${phrase}`,
                  actions: [cancelAction],
                });
                return;                  
              }

              deleteAction = { text: "Delete", primary: true };
              dialog = this.dialogService.open({
                title: "Please confirm",
                content: "Deleting this configuration key cannot be undone.",
                actions: [cancelAction, deleteAction],
              });
              
              this.WaitForDeleteConfirmation(dialog, deleteAction, dataItem);
            })
        });
    } else {
      deleteAction = { text: "Remove", primary: true };
      dialog = this.dialogService.open({
        title: "Please confirm",
        content:
          "Removing this override will cause the value to fallback to the default.",
        actions: [cancelAction, deleteAction],
      });

      this.WaitForDeleteConfirmation(dialog, deleteAction, dataItem);
    }  
  }

  private WaitForDeleteConfirmation(dialog: DialogRef, deleteAction, dataItem) {
    dialog.result.subscribe((result) => {
      if (result === deleteAction) {
        // service
        if (this.scope === ServiceConfigurationScope.Service) {
          this.refreshObservable(
            this.serviceConfigurationKeyDataService.delete(dataItem.Id)
          );
        } else if (this.scope === ServiceConfigurationScope.Environment) {
          // environment
          this.refreshObservable(
            this.environmentServiceConfigurationDataService.delete(
              dataItem.EnvironmentServiceConfigurationId
            )
          );
        } else if (this.scope === ServiceConfigurationScope.Tenant) {
          // tenant
          this.refreshObservable(
            this.tenantEnvironmentServiceConfigurationDataService.delete(
              dataItem.TenantEnvironmentServiceConfigurationId
            )
          );
        }
      }
    })
  }
}
